import { downloadCSV } from "@/lib/downloadCSV";
import type { ReportColumn } from "../components/reports/types";

function escapeCsvCell(value: string): string {
  const normalized = value.replace(/"/g, '""');
  if (/[",\n\r]/.test(normalized)) {
    return `"${normalized}"`;
  }
  return normalized;
}

function serializeExportValue(value: unknown): string {
  if (value === null || value === undefined) {return "";}
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }
  if (value instanceof Date) {return value.toISOString();}
  return JSON.stringify(value);
}

/** Flatten an API object so every response field is available for CSV export */
export function apiItemToExportRecord(item: object): Record<string, string> {
  const record: Record<string, string> = {};
  for (const [key, value] of Object.entries(item as Record<string, unknown>)) {
    record[key] = serializeExportValue(value);
  }
  return record;
}

function collectExportColumns(records: Record<string, string>[]): string[] {
  const columns: string[] = [];
  const seen = new Set<string>();
  for (const record of records) {
    for (const key of Object.keys(record)) {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    }
  }
  return columns;
}

/** Export all fields from API records (objects / arrays serialized as JSON) */
export function exportRecordsToCsv(
  records: Record<string, string>[],
  fileName: string
): void {
  if (records.length === 0) {return;}

  const columns = collectExportColumns(records);
  const header = columns.map(col => escapeCsvCell(col)).join(",");
  const body = records
    .map(record =>
      columns.map(col => escapeCsvCell(record[col] ?? "")).join(",")
    )
    .join("\n");

  downloadCSV(`${header}\n${body}`, fileName);
}

export function exportReportToCsv<T extends { exportData: Record<string, string> }>(
  rows: T[],
  fileName: string
): void {
  exportRecordsToCsv(
    rows.map(row => row.exportData),
    fileName
  );
}

/** @deprecated Use exportReportToCsv with exportData rows */
export function exportReportColumnsToCsv<T>(
  rows: T[],
  columns: ReportColumn<T>[],
  fileName: string
): void {
  const header = columns.map(col => escapeCsvCell(col.label)).join(",");
  const body = rows
    .map(row =>
      columns
        .map(col => {
          const raw = col.exportValue
            ? col.exportValue(row)
            : String(col.accessor(row) ?? "");
          return escapeCsvCell(raw);
        })
        .join(",")
    )
    .join("\n");

  downloadCSV(`${header}\n${body}`, fileName);
}
